<template>
  <nav
    class="side-arrow absolute flex items-center justify-center"
    :aria-label="label"
    :class="{
      'right-0': side === 'right',
      active:
        visibilityStore.showUI ||
        (wpsClient.media.isMediaOverlay &&
          !settingsStore.perContent.ttsAutoTurn),
      // TODO: why does side arrows need to be different for PDF?
      'side-arrow--pdf': wpsClient.media.isPDF,
    }"
    @click="click"
  >
    <AccessibleButton
      class="square-icon-button"
      :title="label"
      :aria-label="label"
      :show-on-hover="false"
    >
      <template #icon>
        <span class="material-icons side-arrow__icon" aria-hidden="true">
          {{ icon }}
        </span>
      </template>
    </AccessibleButton>
  </nav>
</template>

<script setup lang="ts">
import { computed, inject, Ref } from "vue";
import { useI18n } from "vue-i18n";

import { useSettingsStore } from "@/stores/settings";
import { useUiVisibilityStore } from "@/stores/uiVisibility";

import AccessibleButton from "@/components/shared/AccessibleButton.vue";
import { useWpsClient } from "@/wps/client";
import type { Direction } from "../navigator/types";
import type MediaPlayer from "@/services/media/player";

const { t } = useI18n();

const wpsClient = useWpsClient();

const settingsStore = useSettingsStore();
const visibilityStore = useUiVisibilityStore();

const props = defineProps<{
  side: "left" | "right";
  goTo(direction: Direction, keepUI?: boolean): void;
}>();

const mediaPlayer = inject("mediaPlayer") as Ref<MediaPlayer | null>;

async function click(): Promise<void> {
  const keepUI = !!mediaPlayer.value;
  await props.goTo(props.side === "left" ? "previous" : "next", keepUI);
}

const label = computed(() => {
  return props.side === "left"
    ? t("sideArrows.leftArrow")
    : t("sideArrows.rightArrow");
});

const icon = computed(() => {
  return props.side === "left" ? "arrow_back_ios_new" : "arrow_forward_ios";
});
</script>

<style lang="scss" scoped>
// imported reader instead variables because here is used .square-icon-button
@use "@/assets/styles/reader.scss" as *;
.side-arrow {
  height: $spacingXL;
  width: $spacingL;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
  z-index: 2;
  &.active {
    opacity: 1;
  }
  &__icon {
    user-select: none;
    @media (any-hover: hover) {
      transition: all 0.2s ease;
      &:hover {
        opacity: 0.5;
      }
    }
  }
  &--pdf {
    z-index: 2;
    height: calc(100% - 2 * $barHeight);
    &:after {
      content: "";
      opacity: 0.4;
      position: absolute;
      width: 24px;
      height: 40px;
      background: var(--DM__theme-bg-color);
      z-index: -1;
    }
  }

  @media (min-width: $screenM) {
    width: $spacingXXL;
  }

  @media (any-hover: hover) {
    cursor: pointer;
    height: 4 * $spacingXXL;
    &:hover {
      transition: opacity 0.4s ease;
      opacity: 1;
    }
    &--pdf {
      height: calc(100% - 2 * $barHeight);
    }
  }

  @media (min-width: $screenXL) {
    opacity: 1;
  }
}
</style>
